import { FadingScrollable } from '@renderer/chat'
import { Tooltip } from '@highlight/ui'
import { useMemo } from 'react'
import { groupEmailsByDomain, sortDomains, formatDomainName } from '@renderer/utils/email-utils'
import { hashColors } from '@renderer/overlay/utils/hash-color'

interface ParticipantsProps {
  participants: Array<string>
  hideTooltip?: boolean
  size?: keyof typeof BADGE_SIZES
}

function Participants({ participants, hideTooltip = false, size = 'default' }: ParticipantsProps) {
  if (!participants || participants.length === 0) {
    return (
      <div className="text-light/40 bg-light-5 h-[18px] rounded-full px-2 pt-px text-xs font-normal">No attendees</div>
    )
  }

  if (hideTooltip) {
    return <ParticipantBadges participants={participants} size={size} />
  }

  return (
    <Tooltip>
      <Tooltip.Trigger render={<div className="flex w-fit" />}>
        <ParticipantBadges participants={participants} size={size} />
      </Tooltip.Trigger>
      <Tooltip.Content>
        <ParticipantList participants={participants} />
      </Tooltip.Content>
    </Tooltip>
  )
}

/** Participants' emails grouped under their domains, scrolling past a few rows. */
function ParticipantList({ participants }: { participants: Array<string> }) {
  const grouped = useMemo(() => groupEmailsByDomain(participants), [participants])
  const domains = useMemo(() => sortDomains(Object.keys(grouped)), [grouped])

  return (
    <FadingScrollable className="flex max-h-36 flex-col gap-2 py-1">
      {domains.map((domain) => {
        const domainParticipants = grouped[domain]
        return (
          <div key={domain} className="flex flex-col gap-0.5">
            <div className="text-foreground-secondary text-xs font-medium">{formatDomainName(domain)}</div>
            <div className="text-foreground flex flex-col font-medium">
              {domainParticipants.map((participant, index) => (
                <span key={`${domain}-${index}`} className="py-0.5 text-sm">
                  {participant}
                </span>
              ))}
            </div>
          </div>
        )
      })}
    </FadingScrollable>
  )
}

const BADGE_18 = {
  avatarSize: 'size-[18px]',
  fontSize: 'text-xs',
  overflowFontSize: 'text-[10px]',
  maskRadius: '8px',
  maskPosition: '22px',
  overlap: '-3px',
  overflowHeight: '18px',
}

const BADGE_SIZES = {
  default: BADGE_18,
  sm: BADGE_18,
  // The 12px of the icons beside it, for a row's subtitle.
  xs: {
    avatarSize: 'size-3',
    fontSize: 'text-[8px] leading-none',
    overflowFontSize: 'text-[8px] leading-none',
    maskRadius: '5px',
    maskPosition: '13px',
    overlap: '-3px',
    overflowHeight: '12px',
  },
}

interface ParticipantBadgesProps {
  participants: Array<string>
  size?: keyof typeof BADGE_SIZES
}

function ParticipantBadges({ participants, size = 'sm' }: ParticipantBadgesProps) {
  const PARTICIPANTS_DISPLAY_COUNT = 3
  const visibleParticipants = participants?.slice(0, PARTICIPANTS_DISPLAY_COUNT) || []
  const colorClasses = hashColors(visibleParticipants)
  const sizeConfig = BADGE_SIZES[size]

  // Memoize mask style template
  const maskStyleTemplate = useMemo(
    () => ({
      maskImage: `radial-gradient(circle at ${sizeConfig.maskPosition} 50%, transparent ${sizeConfig.maskRadius}, black ${parseInt(sizeConfig.maskRadius) + 1}px)`,
      WebkitMaskImage: `radial-gradient(circle at ${sizeConfig.maskPosition} 50%, transparent ${sizeConfig.maskRadius}, black ${parseInt(sizeConfig.maskRadius) + 1}px)`,
    }),
    [sizeConfig.maskPosition, sizeConfig.maskRadius],
  )

  return (
    <div className={`my-auto flex items-center ${sizeConfig.fontSize} font-medium`}>
      <div className="relative isolate flex items-center">
        {visibleParticipants.map((participant, index) => {
          const isLastBadge = index === visibleParticipants.length - 1
          const shouldShowMask = !(isLastBadge && participants?.length <= PARTICIPANTS_DISPLAY_COUNT)
          const colorClass = colorClasses[index]

          return (
            <span
              key={index}
              className={`relative flex ${sizeConfig.avatarSize} ${colorClass} items-center justify-center rounded-full first:ml-0`}
              style={{
                marginLeft: index === 0 ? '0' : sizeConfig.overlap,
                zIndex: visibleParticipants.length - index,
                ...(shouldShowMask ? maskStyleTemplate : {}),
              }}
            >
              {(participant || '?').slice(0, 1).toUpperCase()}
            </span>
          )
        })}
      </div>

      {participants?.length > PARTICIPANTS_DISPLAY_COUNT && (
        <span
          className={`text-foreground-placeholder bg-background-subtle relative flex min-w-8 items-center justify-center rounded-full ${sizeConfig.overflowFontSize}`}
          style={{
            marginLeft: sizeConfig.overlap,
            height: sizeConfig.overflowHeight,
          }}
        >
          +{participants.length - PARTICIPANTS_DISPLAY_COUNT}
        </span>
      )}
    </div>
  )
}

export { Participants, ParticipantList }
